/**
 * BillableItemTemplatesTab Component
 * Settings page tab for managing billable item template library
 * Phase 8 Batch 8.6 - Billable Item Templates
 *
 * Manages tenant-scoped billable item templates for quick-add to projects.
 * Users can add, edit, and delete reusable templates.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Pencil, Trash2, Plus, X, Check, XCircle } from 'lucide-react';
import {
  useLibraryTemplates,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
  type BillableItemTemplate,
  type BillableItemCategory,
} from '@/hooks/useBillableItemTemplates';

// Category labels for display
const CATEGORY_LABELS: Record<BillableItemCategory, string> = {
  professional_fee: 'Professional Fee',
  add_service: 'Additional Service',
  billable_expense: 'Billable Expense',
  pass_through: 'Pass Through',
  internal_work: 'Internal Work',
};

// Category options for select
const CATEGORY_OPTIONS: BillableItemCategory[] = [
  'professional_fee',
  'add_service',
  'billable_expense',
  'pass_through',
  'internal_work',
];

export function BillableItemTemplatesTab() {
  const { data, isLoading } = useLibraryTemplates();
  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();
  const deleteMutation = useDeleteTemplate();

  // New template form state
  const [newDescription, setNewDescription] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [newDeliverables, setNewDeliverables] = useState('');
  const [newCategory, setNewCategory] = useState<BillableItemCategory>('professional_fee');
  const [keepValuesAfterAdd, setKeepValuesAfterAdd] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editDeliverables, setEditDeliverables] = useState('');
  const [editCategory, setEditCategory] = useState<BillableItemCategory>('professional_fee');

  const templates = data?.templates || [];

  const handleAdd = () => {
    if (!newDescription.trim() || !newAmount.trim()) return;

    const amount = parseFloat(newAmount);
    if (isNaN(amount) || amount < 0) return;

    createMutation.mutate(
      {
        description: newDescription.trim(),
        suggested_amount: amount,
        deliverables: newDeliverables.trim() || null,
        category: newCategory,
      },
      {
        onSuccess: () => {
          // Always clear description and amount
          setNewDescription('');
          setNewAmount('');

          // Only clear category and deliverables if "keep values" is disabled
          if (!keepValuesAfterAdd) {
            setNewDeliverables('');
            setNewCategory('professional_fee');
          }
        },
      }
    );
  };

  const handleClearForm = () => {
    setNewDescription('');
    setNewAmount('');
    setNewDeliverables('');
    setNewCategory('professional_fee');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAdd();
    } else if (e.key === 'Escape') {
      handleClearForm();
    }
  };

  const handleStartEdit = (template: BillableItemTemplate) => {
    setEditingId(template.id);
    setEditDescription(template.description);
    setEditAmount(String(template.suggested_amount));
    setEditDeliverables(template.deliverables || '');
    setEditCategory(template.category);
  };

  const handleSaveEdit = () => {
    if (!editingId || !editDescription.trim() || !editAmount.trim()) return;

    const amount = parseFloat(editAmount);
    if (isNaN(amount) || amount < 0) return;

    updateMutation.mutate(
      {
        id: editingId,
        description: editDescription.trim(),
        suggested_amount: amount,
        deliverables: editDeliverables.trim() || null,
        category: editCategory,
      },
      {
        onSuccess: () => {
          setEditingId(null);
          setEditDescription('');
          setEditAmount('');
          setEditDeliverables('');
          setEditCategory('professional_fee');
        },
      }
    );
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditDescription('');
    setEditAmount('');
    setEditDeliverables('');
    setEditCategory('professional_fee');
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this template?')) {
      deleteMutation.mutate(id);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="space-y-1">
        <h2 className="text-2xl font-bold tracking-tight">Billable Item Templates Library</h2>
        <p className="text-muted-foreground">
          Manage reusable billable item templates for quick-add when creating proposals and projects.
        </p>
      </div>

      {/* Help Text */}
      <div className="rounded-lg border bg-muted/50 p-4">
        <p className="text-sm text-muted-foreground mb-2">
          Create a library of commonly-used billable items. When creating projects, you can
          quickly add these items from a searchable dropdown instead of typing them out each time.
        </p>
        <p className="text-sm text-muted-foreground">
          <strong>Examples:</strong> "Project Startup Fee - $1,250" or "Building Permit Package - $1,500"
        </p>
      </div>

      {/* Add New Template Form - QF-04: Moved above list for better UX */}
      <div className="space-y-4 rounded-lg border p-4">
        <Label className="text-lg font-semibold">Add New Template</Label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="new-description">Description *</Label>
            <Input
              id="new-description"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g., Building Permit Package"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="new-amount">Default Amount *</Label>
              <Input
                id="new-amount"
                type="number"
                min="0"
                step="0.01"
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="1500.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-category">Category</Label>
              <Select
                value={newCategory}
                onValueChange={(value) => setNewCategory(value as BillableItemCategory)}
              >
                <SelectTrigger id="new-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {CATEGORY_LABELS[cat]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-deliverables">Deliverables (optional)</Label>
          <Textarea
            id="new-deliverables"
            value={newDeliverables}
            onChange={(e) => setNewDeliverables(e.target.value)}
            placeholder="Enter deliverables (one per line), e.g.:&#10;- Architectural drawings&#10;- Site plan&#10;- Permit application forms"
            rows={3}
          />
        </div>

        {/* Bulk creation helper */}
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Checkbox
            id="keep-values"
            checked={keepValuesAfterAdd}
            onCheckedChange={(checked) => setKeepValuesAfterAdd(checked as boolean)}
          />
          <Label
            htmlFor="keep-values"
            className="text-sm font-normal cursor-pointer"
          >
            Keep category and deliverables after adding (for bulk creation)
          </Label>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleAdd}
            disabled={!newDescription.trim() || !newAmount.trim() || createMutation.isPending}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Template
          </Button>
          <Button
            onClick={handleClearForm}
            variant="outline"
            disabled={createMutation.isPending}
          >
            <XCircle className="h-4 w-4 mr-2" />
            Clear
          </Button>
        </div>

        {/* Keyboard shortcuts hint */}
        <p className="text-xs text-muted-foreground">
          Tip: Press <kbd className="px-1.5 py-0.5 text-xs font-semibold border rounded">Enter</kbd> to add, <kbd className="px-1.5 py-0.5 text-xs font-semibold border rounded">Esc</kbd> to clear
        </p>
      </div>

      {/* Templates List */}
      <div className="space-y-4">
        <Label>Templates ({templates.length})</Label>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : templates.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <p className="text-sm text-muted-foreground">
              No billable item templates yet. Add your first one above.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {templates.map((template) => (
              <div
                key={template.id}
                className="rounded-md border p-4 hover:bg-muted/50"
              >
                {editingId === template.id ? (
                  // Edit mode
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor={`edit-description-${template.id}`}>Description</Label>
                        <Input
                          id={`edit-description-${template.id}`}
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`edit-amount-${template.id}`}>Amount</Label>
                          <Input
                            id={`edit-amount-${template.id}`}
                            type="number"
                            min="0"
                            step="0.01"
                            value={editAmount}
                            onChange={(e) => setEditAmount(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`edit-category-${template.id}`}>Category</Label>
                          <Select
                            value={editCategory}
                            onValueChange={(value) => setEditCategory(value as BillableItemCategory)}
                          >
                            <SelectTrigger id={`edit-category-${template.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CATEGORY_OPTIONS.map((cat) => (
                                <SelectItem key={cat} value={cat}>
                                  {CATEGORY_LABELS[cat]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`edit-deliverables-${template.id}`}>Deliverables (optional)</Label>
                      <Textarea
                        id={`edit-deliverables-${template.id}`}
                        value={editDeliverables}
                        onChange={(e) => setEditDeliverables(e.target.value)}
                        placeholder="Enter deliverables (one per line)"
                        rows={3}
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCancelEdit}
                        disabled={updateMutation.isPending}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveEdit}
                        disabled={updateMutation.isPending || !editDescription.trim() || !editAmount.trim()}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Display mode
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{template.description}</p>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-muted">
                          {CATEGORY_LABELS[template.category]}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {formatCurrency(template.suggested_amount)}
                      </p>
                      {template.deliverables && (
                        <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">
                          {template.deliverables.length > 100
                            ? `${template.deliverables.substring(0, 100)}...`
                            : template.deliverables}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleStartEdit(template)}
                        aria-label="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(template.id)}
                        disabled={deleteMutation.isPending}
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
